// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { css } from '@emotion/css';
import { Container, TextField, Theme, Typography, SxProps, useTheme, InputAdornment } from '@mui/material';
import { BiSearch } from 'react-icons/bi';

import SearchCriteriaIcon from '@components/SearchCriteriaIcon';

// COMPONENT ---------------------------------------------------------------- //

const SearchResultsLanding: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const cls = useStyles(theme);
  const sxStyles = useSxStyles(theme);

  const [search, setSearch] = React.useState<string>(``);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.value?.length > 48) return;
    setSearch(event?.target?.value);
  };

  const handleSearchSubmit = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const editedSearch = search?.trim()?.toLowerCase();

    if (!editedSearch) {
      toast.warning('Please enter a search term.');
    } else if (editedSearch && editedSearch.length < 3) {
      toast.warning('Please enter at least 3 characters to search on.');
    } else if (editedSearch && editedSearch.length > 48) {
      toast.warning('Please do not enter more than 48 characters to search on.');
    } else {
      navigate('/search-moves', { state: { searchInput: editedSearch } });
    }
  };

  return (
    <div className={cls.container}>
      <Container maxWidth='sm'>
        <Typography sx={sxStyles.titleText}>Search for Moves</Typography>

        <TextField
          autoFocus
          fullWidth
          placeholder='Enter a full or partial search term...'
          value={search}
          onChange={handleSearchChange}
          onKeyUp={e => {
            if (e.key === `Enter`) {
              handleSearchSubmit(e);
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <BiSearch size={20} color={theme.palette.text.disabled} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position='end'>
                  <SearchCriteriaIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Container>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => {
  return {
    container: css`
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: calc(100vh - 200px);
      text-align: center;
    `,
  };
};

const useSxStyles = (theme: Theme): Record<string, SxProps<Theme> | undefined> => ({
  titleText: {
    marginBottom: theme.spacing(2),
    lineHeight: 1,
    fontSize: 24,
    fontWeight: 600,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default SearchResultsLanding;
